import { useMemo, useEffect } from 'react';
import { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { useAtom, useSetAtom } from 'jotai';
import debounce from 'lodash.debounce';

import { useQueryClientData } from '@restworld/utils-common';
import { RestaurantsNamesReturnType } from '@restworld/data-services';
import { Alert, SkeletonTextLoading } from '@restworld/ui-components';
import { getServerTranslations } from '../../../get-server-side-translations';
import { EmployerPrimaryLayout } from '../../../layouts';
import {
  ShowHiddenCandidatesFilter,
  SpontaneousCandidateRestaurantFilter,
} from '../../../components/spontaneous-candidates/employer-spontaneous-candidates-filters';
import EmployerSpontaneousCandidatesContent from '../../../components/spontaneous-candidates/employer-spontaneous-candidates-content.tsx';
import { useEmployerJobApplicationsInfiniteQuery } from '../../../components/job-listing/hooks/use-employer-job-applications';
import {
  employerSpontaneousCandidatesSearchQueryAtom,
  employerSpontaneousCandidatesRestaurantFilterAtom,
  employerSpontaneousCandidatesShowHiddenFilterAtom,
} from '../../../components/spontaneous-candidates/employer-spontaneous-candidates.atoms';
import { useEmployerRestaurantNamesQueryKey } from '../../../components/restaurants/hooks/use-employer-restaurant-names';
import { employerJobApplicationsInfiniteQueryParamsAtom } from '../../../components/job-listing/employer-job-listing.atoms';
import useSpontaneousCandidatesFiltersInUrl from '../../../components/spontaneous-candidates/hooks/use-spontaneous-candidates-filters-in-url';
import { useIntercom } from 'react-use-intercom';

function SpontaneousCandidatesPage() {
  const { t } = useTranslation('spontaneous-candidates-page');
  const { startTour } = useIntercom();
  const [searchQuery, setSearchQuery] = useAtom(employerSpontaneousCandidatesSearchQueryAtom);
  const [showHiddenFilter, setShowHiddenFilter] = useAtom(employerSpontaneousCandidatesShowHiddenFilterAtom);
  const [restaurantFilter, setRestaurantFilter] = useAtom(employerSpontaneousCandidatesRestaurantFilterAtom);
  const queryKey = useEmployerRestaurantNamesQueryKey();
  const restaurantNames: RestaurantsNamesReturnType[] = useQueryClientData(queryKey);
  const setEmployerJobApplicationsInfiniteQueryParams = useSetAtom(employerJobApplicationsInfiniteQueryParamsAtom);
  useSpontaneousCandidatesFiltersInUrl({
    restaurantNames,
    setRestaurantFilter,
    setShowHiddenFilter,
  });

  const params = useMemo(() => {
    const baseParams = {
      spontaneous_applications: true,
      show_worker_distance: true,
      ...(searchQuery && { search_vector: searchQuery }),
      ...(!showHiddenFilter && { hidden: showHiddenFilter }),
      ...(restaurantFilter?.id && { restaurantId: restaurantFilter.id }),
    };

    return baseParams;
  }, [restaurantFilter?.id, searchQuery, showHiddenFilter]);

  const { jobApplications, hasNextPage, hits, query } = useEmployerJobApplicationsInfiniteQuery(params, 0);

  const foundNoResults = useMemo(
    () => !query.isLoading && jobApplications?.length === 0,
    [jobApplications?.length, query.isLoading]
  );
  const totalSearchResults = query.isLoading ? (
    <SkeletonTextLoading width="medium" className="mt-2" />
  ) : (
    hits &&
    (hits === 1 ? `${hits} ${t('spontaneous_application_key')}` : `${hits} ${t('spontaneous_applications_title')}`)
  );

  useEffect(() => {
    setEmployerJobApplicationsInfiniteQueryParams(params);
  }, [params, setEmployerJobApplicationsInfiniteQueryParams]);

  return (
    <EmployerPrimaryLayout
      additionalFilters={
        <>
          <ShowHiddenCandidatesFilter />
          <SpontaneousCandidateRestaurantFilter />
        </>
      }
      loadMoreBtnProps={{
        onClick: () => query.fetchNextPage(),
        loading: query.isFetchingNextPage,
        hasNextPage,
      }}
      noResultsFoundProps={{
        foundNoResults,
        title: t('there_are_no_spontaneous_candidates_at_the_moment'),
        content: t('make_sure_you_shared_your_showcase_page_on_social_media_and_printed_your_personalized_qr_code'),
      }}
      pageTitle={t('spontaneous_applications_title')}
      searchProps={{
        placeholder: t('insert_the_name_of_the_candidate'),
        onChange: debounce((value) => setSearchQuery(value), 500),
      }}
      title={t('spontaneous_applications_title')}
      totalSearchResults={totalSearchResults}
      loading={query.isLoading}
      onInfoIconClick={() => startTour(599326)}
      renderCustomElement={
        hits >= 1 && (
          <Alert
            variant="warning"
            text={t(
              'these_are_spontaneous_applications_made_directly_by_people_and_are_not_managed_by_the_restworld_manager'
            )}
          />
        )
      }
    >
      <EmployerSpontaneousCandidatesContent jobApplications={jobApplications} isLoading={query?.isLoading} />
    </EmployerPrimaryLayout>
  );
}
export default SpontaneousCandidatesPage;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translation = await getServerTranslations(locale, [
    'common',
    'onboarding-page',
    'spontaneous-candidates-page',
    'job-positions-page',
  ]);
  return {
    props: {
      ...translation,
    },
  };
};
